import { setup, assign, fromCallback, and } from "xstate"

type Response = { [value: string]: number | null }

export interface DinoMachineContext {
  page: number
  nextPage: number
  waiting: boolean
  allResponse: Response[]
  sheets: any[][]
  contentCount: number
  splicedContentIds: number[]
}

export type DinoMachineEvent =
  | { type: "SHEETS_LOADED"; sheets: any[][]; contentCount: number }
  | { type: "PUT_RESPONSE"; response: Response }
  | { type: "GO_NEXT"; wait?: boolean }
  | { type: "GO_PREV" }
  | { type: "SPLICE_SHEETS"; contentId: number }
  | { type: "WAIT_COMPLETE" }
  | { type: "SUBMISSION_COMPLETE" }

export const dinoMachine = setup({
  types: {
    context: {} as DinoMachineContext,
    events: {} as DinoMachineEvent,
  },
  actors: {
    waitTimer: fromCallback(({ sendBack }) => {
      const id = setTimeout(() => sendBack({ type: "WAIT_COMPLETE" }), 200)
      return () => clearTimeout(id)
    }),
    submissionTimer: fromCallback(({ sendBack }) => {
      const id = setTimeout(() => sendBack({ type: "SUBMISSION_COMPLETE" }), 3000)
      return () => clearTimeout(id)
    }),
  },
  guards: {
    allAnswersFilled: ({ event }) => {
      if (event.type !== "PUT_RESPONSE") return false
      const values = Object.values(event.response)
      return values.length > 0 && values.every((v) => !!v)
    },
    hasNextPage: ({ context }) => context.page < context.sheets.length - 1,
    hasPrevPage: ({ context }) => context.page > 0,
    isFinished: ({ context }) =>
      context.sheets.length > 0 && context.sheets[context.page]?.[0] === "FINISHED",
    needsWait: ({ context }) => context.waiting,
    notYetSpliced: ({ context, event }) => {
      if (event.type !== "SPLICE_SHEETS") return false
      return !context.splicedContentIds.includes(event.contentId)
    },
  },
  actions: {
    setResponse: assign(({ context, event }) => {
      if (event.type !== "PUT_RESPONSE") return {}
      const newAllResponse = [...context.allResponse]
      newAllResponse[context.page] = event.response
      return { allResponse: newAllResponse }
    }),
    prepareAutoAdvance: assign(({ context }) => ({
      nextPage: context.page + 1,
      waiting: true,
    })),
    applyPageTransition: assign(({ context }) => ({
      page: context.nextPage,
      waiting: false,
    })),
    loadSheets: assign(({ event }) => {
      if (event.type !== "SHEETS_LOADED") return {}
      return { sheets: event.sheets, contentCount: event.contentCount }
    }),
  },
}).createMachine({
  id: "dino",
  initial: "loading",
  context: {
    page: 0,
    nextPage: 0,
    waiting: false,
    allResponse: [],
    sheets: [],
    contentCount: 0,
    splicedContentIds: [],
  },
  states: {
    loading: {
      on: {
        SHEETS_LOADED: {
          target: "active",
          actions: "loadSheets",
        },
      },
    },
    active: {
      initial: "idle",
      states: {
        idle: {
          always: {
            guard: "isFinished",
            target: "#dino.submitting",
          },
          on: {
            PUT_RESPONSE: [
              {
                guard: and(["allAnswersFilled", "hasNextPage"]),
                actions: ["setResponse", "prepareAutoAdvance"],
                target: "transitioning",
              },
              {
                actions: ["setResponse"],
              },
            ],
            GO_NEXT: {
              guard: "hasNextPage",
              actions: assign(({ context, event }) => ({
                nextPage: context.page + 1,
                waiting: event.type === "GO_NEXT" ? !!event.wait : false,
              })),
              target: "transitioning",
            },
            GO_PREV: {
              guard: "hasPrevPage",
              actions: assign(({ context }) => ({
                nextPage: context.page - 1,
                waiting: false,
              })),
              target: "transitioning",
            },
            SPLICE_SHEETS: {
              guard: "notYetSpliced",
              actions: assign(({ context, event }) => {
                if (event.type !== "SPLICE_SHEETS") return {}
                const newSheets = [...context.sheets]
                const contentLength = Math.floor(newSheets.length / context.contentCount)
                newSheets.splice(event.contentId * contentLength, contentLength)
                return {
                  sheets: newSheets,
                  splicedContentIds: [...context.splicedContentIds, event.contentId],
                }
              }),
            },
          },
        },
        transitioning: {
          always: [
            { guard: "needsWait", target: "waiting" },
            { target: "idle", actions: "applyPageTransition" },
          ],
        },
        waiting: {
          invoke: { src: "waitTimer" },
          on: {
            WAIT_COMPLETE: {
              target: "idle",
              actions: "applyPageTransition",
            },
          },
        },
      },
    },
    submitting: {
      invoke: { src: "submissionTimer" },
      on: {
        SUBMISSION_COMPLETE: "processing",
      },
    },
    processing: {
      type: "final",
    },
  },
})
